import styled from 'styled-components';
import { colors } from '@config/colors';
import { CALM_DEPTH, rgba } from './water-field';

const withAlpha = (hex: string, alpha: number) => {
  const [r, g, b] = rgba(hex).map((c) => Math.round(c * 255));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const surfaceStops = [colors.brand.primaryDark, colors.brand.primary, colors.brand.primaryLight];
const depthStops = [colors.brand.extraDark, colors.brand.primaryDark, colors.brand.primary];

const gradients = (angle: string) =>
  [
    `linear-gradient(${angle}, ${depthStops.map((c, i) => `${withAlpha(c, CALM_DEPTH)} ${i * 50}%`).join(', ')})`,
    `linear-gradient(${angle}, ${surfaceStops.map((c, i) => `${c} ${i * 50}%`).join(', ')})`,
  ].join(', ');

/**
 * The water's calm surface in CSS: the surface ramp with the depth ramp
 * over it at the calm depth — the same two layers the shader starts
 * from, so the canvas fades in over its own ground. It is also the whole
 * background wherever WebGL is unavailable.
 *
 * The angle follows the box's own corner-to-corner diagonal, as the
 * app's gradient does. `to bottom right` would not: on a non-square box
 * CSS aims that at a different line. Browsers without CSS trig keep the
 * 135° declaration.
 */
export const Layer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  height: 100lvh;
  z-index: -1;
  pointer-events: none;
  background-color: ${colors.wee.page};
  background-image: ${gradients('135deg')};
  background-image: ${gradients('calc(180deg - atan2(100vw, 100lvh))')};
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`;

/** Hidden until its first frame has drawn, then fades in over the CSS surface. */
export const WaterCanvas = styled(Canvas)<{ $lit: boolean }>`
  opacity: ${({ $lit }) => ($lit ? 1 : 0)};
  transition: opacity 900ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StarsCanvas = Canvas;

/* How we build's night: the page's lights going down — the slate of the
   app's night mode drawn over the water and under the glints, which stay
   bright like stars. Its opacity is the story's night, set every frame. */
export const NightVeil = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  background: linear-gradient(
    135deg,
    ${colors.neutral.extraDark} 0%,
    ${colors.neutral.dark} 25%,
    ${colors.neutral.darkMedium} 50%,
    ${colors.neutral.dark} 75%,
    ${colors.neutral.extraDark} 100%
  );
`;
